import { Radio, type RadioGroupProps } from "components/Radio/Radio"
import { Tag } from "components/Tag/Tag"

import { SearchInput } from "components/SearchInput/SearchInput"
import { Table } from "components/Table/Table"
import { useTableParams } from "components/Table/useTableParams"
import { AdminCareOrganization } from "interface/careOrganization"
import { ComponentProps, memo, useCallback, useMemo } from "react"
import { useAdminCareOrganizations } from "services/organizations"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { asDate, DATE_TIME_FORMAT } from "utils/date"
import { CreateCareOrganization } from "./CreateCareOrganization"
import { UpdateCareOrganization } from "./UpdateCareOrganization"

const DEFAULT_SORT = ["name-ascending"]

export const AdminCareOrganizations = () => {
	const { set, page, sort, query, excludedFromAnalytics } = useTableParams({
		sort: DEFAULT_SORT,
		excludedFromAnalytics: "all",
	})
	const { data: { result: careOrganizations = [], total } = {}, isFetching } =
		useAdminCareOrganizations({
			sort,
			page,
			query,
			excludedFromAnalytics:
				excludedFromAnalytics === "all"
					? undefined
					: excludedFromAnalytics === "true",
		})
	const handleTableChange = useCallback<
		NonNullable<TableCareOrganizationsType["onChange"]>
	>(
		(pagination, _, sorter) => {
			const { field, order } = Array.isArray(sorter) ? sorter[0] : sorter
			set(
				["page", String(pagination.current)],
				["sort", field && order ? [`${field}-${order}ing`] : DEFAULT_SORT],
			)
		},
		[set],
	)
	const handleSearch = useCallback<
		NonNullable<ComponentProps<typeof SearchInput>["onSearch"]>
	>((value) => set(["query", value]), [set])
	const columns = useMemo(() => withSortOrder(COLUMNS, sort[0]), [sort])

	return (
		<div className="flex h-full min-h-0 flex-col gap-3">
			<div className="flex justify-between gap-3">
				<h2 className="font-nunito text-3xl font-semibold">
					Care organizations
				</h2>
				<CreateCareOrganization />
			</div>

			<div className="flex h-full min-h-0 flex-col gap-2">
				<div className="flex flex-wrap gap-3">
					<SearchInput
						size="large"
						value={query}
						className="max-w-xl"
						placeholder="Search by name, timezone or ID"
						onSearch={handleSearch}
					/>
					<ToggleExcludedFromAnalytics
						value={excludedFromAnalytics}
						onChange={({ target: { value } }) =>
							set(["excludedFromAnalytics", value])
						}
					/>
				</div>

				<Table<AdminCareOrganization>
					dataSource={careOrganizations}
					columns={columns}
					rowKey="id"
					loading={isFetching}
					pagination={{
						total,
						current: Number(page),
					}}
					onChange={handleTableChange}
					onRow={({ name }) => ({
						"aria-label": `Care organization ${name}`,
					})}
				/>
			</div>
		</div>
	)
}

const ToggleExcludedFromAnalytics = memo(function ToggleExcludedFromAnalytics(
	props: RadioGroupProps,
) {
	return (
		<div
			role="group"
			aria-label="Excluded from analytics"
			className="flex items-center gap-1"
		>
			<div className="text-nowrap">Excluded from analytics</div>
			<Radio.Group
				{...props}
				size="large"
				optionType="button"
				options={[
					{ label: "All", value: "all" },
					{ label: "Yes", value: "true" },
					{ label: "No", value: "false" },
				]}
			/>
		</div>
	)
})

const COLUMNS: NonNullable<TableCareOrganizationsType["columns"]> = [
	{
		key: "operation",
		title: "Actions",
		fixed: "left",
		render(_, careOrganization) {
			return <UpdateCareOrganization careOrganization={careOrganization} />
		},
	},
	{
		dataIndex: "name",
		key: "name",
		title: "Name",
		sorter: true,
	},
	{
		dataIndex: "id",
		key: "id",
		title: "ID",
	},
	{
		dataIndex: "supervisorName",
		key: "supervisorName",
		title: "Supervisor",
	},
	{
		dataIndex: "timezone",
		key: "timezone",
		title: "Timezone",
		sorter: true,
		render(value?: string) {
			return value || DEFAULT_PLACEHOLDER
		},
	},
	{
		dataIndex: "excludedFromAnalytics",
		key: "excludedFromAnalytics",
		title: "Excluded from analytics",
		sorter: true,
		render(value: boolean) {
			return value ? <Tag color="warning">Yes</Tag> : <Tag>No</Tag>
		},
	},
	{
		dataIndex: "createdAt",
		key: "createdAt",
		title: "Created date",
		sorter: true,
		render(value?: string) {
			return value
				? asDate(value).format(`${DATE_TIME_FORMAT} z`)
				: DEFAULT_PLACEHOLDER
		},
	},
]

function withSortOrder(
	columns: NonNullable<TableCareOrganizationsType["columns"]>,
	activeSort?: string,
): NonNullable<TableCareOrganizationsType["columns"]> {
	const [activeField, direction] = activeSort?.split("-") ?? []
	const sortOrder: "ascend" | "descend" | null =
		direction === "ascending"
			? "ascend"
			: direction === "descending"
				? "descend"
				: null

	return columns.map((column) =>
		"sorter" in column && column.sorter
			? { ...column, sortOrder: column.key === activeField ? sortOrder : null }
			: column,
	)
}

type TableCareOrganizationsType = ComponentProps<
	typeof Table<AdminCareOrganization>
>
